import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { PO_LEDGER_SHADOW } from '../../consts/reports';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const display = (value, column) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'currency') return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return value;
};

const PoLedgerShadowReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const defaultEnd = format(new Date(), 'yyyy-MM-dd');
  const defaultStart = format(subDays(new Date(), 364), 'yyyy-MM-dd');
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStart, defaultEnd);
  const [status, setStatus] = useState('all');
  const [vendor, setVendor] = useState('');
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const params = useCallback((all = false) => {
    const query = new URLSearchParams({ page: all ? 1 : page, pageSize: all ? 10000 : PAGE_SIZE });
    query.set('startDate', startDate);
    query.set('endDate', endDate);
    if (status !== 'all') query.set('status', status);
    if (vendor) query.set('vendor', vendor);
    if (exceptionsOnly) query.set('exceptionsOnly', 'true');
    return query;
  }, [page, status, vendor, exceptionsOnly, startDate, endDate]);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${PO_LEDGER_SHADOW}?${params()}`, { headers: authHeaders() });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load PO ledger shadow report');
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setTotalPages(json.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load, startDate, endDate]);
  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${PO_LEDGER_SHADOW}/export?${params(true)}`, { headers: authHeaders() });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'po-ledger-shadow.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  const renderCell = (row, column) => {
    if (column.key === 'po_number') {
      return <Link className="text-blue-700 hover:underline" to={`${ROUTES.PURCHASE_ORDERS}/${row.po_id}`}>{row.po_number || '—'}</Link>;
    }
    if (column.key === 'remaining_liability' && row.reconciliation_status === 'EXCEPTION') return 'DATA NOT RECONCILED';
    if (column.key === 'open_exception_count' && row.open_exceptions?.length) {
      return <span title={row.open_exceptions.map((item) => `${item.check_type}: ${item.suspected_cause || ''}`).join('\n')}>{row.open_exception_count}</span>;
    }
    return display(row[column.key], column);
  };
  const filters = <div className="contents">
    <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={setDateRange} summaryDateFormat="MM/dd/yyyy" pickerDateFormat="MM/dd/yyyy" label="PO date range" />
    <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
      <option value="all">All statuses</option><option value="OPEN">OPEN</option><option value="CLOSED">CLOSED</option><option value="CANCELLED">CANCELLED</option>
    </select>
    <input className="rounded border px-3 py-2 text-sm" value={vendor} onChange={(event) => { setVendor(event.target.value); setPage(1); }} placeholder="Vendor ID" />
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={exceptionsOnly} onChange={(event) => { setExceptionsOnly(event.target.checked); setPage(1); }} /> Exceptions only</label>
  </div>;
  return <ReportTemplate reportId={PO_LEDGER_SHADOW} title="PO Ledger (Shadow)" description="Canonical PO liability projection beside legacy AP figures." breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]} onBack={() => navigate(ROUTES.REPORTS)} marginFromSidebar={marginFromSidebar} isLoading={loading} error={error} filters={filters} exportButton={<CSVExportButton data={data} columns={columns} filename="po-ledger-shadow" onExport={exportData} />} pagination={<div className="flex justify-between text-sm"><span>{data.length} rows</span><div className="flex gap-2"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button></div></div>} renderChildrenOnly>
    <div className="space-y-3">
      <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4"><div><div className="text-xs text-gray-500">Ledger remaining</div><strong>{display(totals.ledger_remaining_liability, { type: 'currency' })}</strong></div><div><div className="text-xs text-gray-500">AP Due</div><strong>{display(totals.ap_due_total_due, { type: 'currency' })}</strong></div><div><div className="text-xs text-gray-500">AP Progress</div><strong>{display(totals.ap_progress_total_due, { type: 'currency' })}</strong></div><div><div className="text-xs text-gray-500">Open exceptions</div><strong>{totals.open_exception_count || 0}</strong></div></div>
      <div className="overflow-auto rounded border bg-white"><table className="min-w-max text-left text-xs"><thead className="sticky top-0 bg-gray-100"><tr>{columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead><tbody>{data.map((row) => <tr key={row.po_id} className={row.reconciliation_status === 'EXCEPTION' ? 'bg-red-50' : ''}>{columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{renderCell(row, column)}</td>)}</tr>)}</tbody></table></div>
    </div>
  </ReportTemplate>;
};

export default PoLedgerShadowReport;
